import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const Gallery = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');

  const content = {
    title: isAr ? 'معرض أعمالنا' : 'Our Work Gallery',
    subtitle: isAr
      ? 'شاهد التحول المذهل الذي نقدمه لكل مركبة مع سوبر كلاسيكي'
      : 'See the transformation we deliver to every vehicle with Super Classy',
    viewDetails: isAr ? 'عرض التفاصيل' : 'View Details',
    categories: {
      all: isAr ? 'الكل' : 'all',
      exterior: isAr ? 'خارجي' : 'exterior',
      interior: isAr ? 'داخلي' : 'interior',
      engine: isAr ? 'المحرك' : 'engine',
      coating: isAr ? 'حماية' : 'coating',
      polish: isAr ? 'تلميع' : 'polish',
      full: isAr ? 'كامل' : 'full',
    },
  };

  const galleryImages = [
    {
      id: 1,
      src: '/assets/images/gallery/car1.jpg',
      alt: 'BMW detailing',
      altAr: 'تلميع بي إم دبليو',
      category: 'exterior',
    },
    {
      id: 2,
      src: '/assets/images/gallery/car2.jpg',
      alt: 'Interior cleaning',
      altAr: 'تنظيف داخلي',
      category: 'interior',
    },
    {
      id: 3,
      src: '/assets/images/gallery/car3.jpg',
      alt: 'Engine cleaning',
      altAr: 'تنظيف محرك',
      category: 'engine',
    },
    {
      id: 4,
      src: '/assets/images/gallery/car4.jpg',
      alt: 'Ceramic coating',
      altAr: 'نانو سيراميك',
      category: 'coating',
    },
    {
      id: 5,
      src: '/assets/images/gallery/car5.jpg',
      alt: 'Wax polish',
      altAr: 'تلميع واكس',
      category: 'polish',
    },
    {
      id: 6,
      src: '/assets/images/gallery/car6.jpg',
      alt: 'Complete detailing',
      altAr: 'تلميع كامل',
      category: 'full',
    },
  ];

  const categoryKeys = [
    'all',
    'exterior',
    'interior',
    'engine',
    'coating',
    'polish',
    'full',
  ];

  const filteredImages =
    activeCategory === 'all'
      ? galleryImages
      : galleryImages.filter((img) => img.category === activeCategory);

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => setSelectedImage(null);

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % filteredImages.length;
    setSelectedImage(filteredImages[nextIndex]);
    setCurrentIndex(nextIndex);
  };

  const prevImage = () => {
    const prevIndex =
      (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[prevIndex]);
    setCurrentIndex(prevIndex);
  };

  return (
    <section
      id='gallery'
      className='section-padding py-20'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='max-w-7xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className='text-center mb-12'
        >
          <h2 className='text-4xl md:text-5xl font-bold mb-4 text-gradient'>
            {content.title}
          </h2>
          <p className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
            {content.subtitle}
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className='flex flex-wrap justify-center gap-2 mb-10'>
          {categoryKeys.map((key) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                activeCategory === key
                  ? 'premium-gradient text-white shadow-lg scale-105'
                  : 'glass-card hover:bg-gray-100 dark:hover:bg-gray-800 dark:text-gray-300'
              }`}
            >
              {content.categories[key]}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
          {filteredImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className='relative group cursor-pointer'
              onClick={() => openLightbox(image, index)}
            >
              <div className='aspect-square overflow-hidden rounded-2xl shadow-md border border-white/10'>
                <img
                  src={image.src}
                  alt={isAr ? image.altAr : image.alt}
                  className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-500'
                />
              </div>
              <div className='absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 rounded-2xl flex items-center justify-center backdrop-blur-[2px] opacity-0 group-hover:opacity-100'>
                <div className='text-white text-lg font-bold border-2 border-white/30 px-6 py-2 rounded-full transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300'>
                  {content.viewDetails}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm'
            onClick={closeLightbox}
          >
            <div
              className='relative max-w-4xl w-full max-h-[90vh]'
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeLightbox}
                className={`absolute top-4 ${isAr ? 'left-4' : 'right-4'} z-10 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white`}
              >
                <X className='h-6 w-6' />
              </button>

              <button
                onClick={isAr ? nextImage : prevImage}
                className={`absolute ${isAr ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white`}
              >
                <ChevronLeft
                  className={`h-8 w-8 ${isAr ? 'rotate-180' : ''}`}
                />
              </button>

              <button
                onClick={isAr ? prevImage : nextImage}
                className={`absolute ${isAr ? 'left-4' : 'right-4'} top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 rounded-full hover:bg-white/20 text-white`}
              >
                <ChevronRight
                  className={`h-8 w-8 ${isAr ? 'rotate-180' : ''}`}
                />
              </button>

              <img
                src={selectedImage.src}
                alt={isAr ? selectedImage.altAr : selectedImage.alt}
                className='w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl'
              />

              <div className='text-center mt-6'>
                <p className='text-white text-xl font-bold'>
                  {isAr ? selectedImage.altAr : selectedImage.alt}
                </p>
                <p className='text-gray-400 mt-2 font-mono'>
                  {currentIndex + 1} / {filteredImages.length}
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
